import {
  RequestsResponse,
  RequestType,
  User,
  UserProfile,
  UsersResponse,
} from "types/userTypes";
import { baseApi } from "./baseApi";
import { PaginationArgs } from "types/globalTypes";
import { buildQueryParams } from "services/helper";
import {
  MajorResponse,
  Responder,
  ResponderResponse,
} from "types/assessmentTypes";

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // user
    getUsers: builder.query<UsersResponse, PaginationArgs>({
      query: (args) => `/admin/user?${buildQueryParams(args || {})}`,
      providesTags: ["Users"],
    }),

    getUserById: builder.query<User, number>({
      query: (id) => `/admin/user/${id}`,
      providesTags: ["Users"],
    }),

    createUser: builder.mutation<User, Partial<User>>({
      query: (newData) => ({
        url: "/admin/user",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Users"],
    }),

    updateUser: builder.mutation<User, { id: number; data: Partial<User> }>({
      query: ({ id, data }) => ({
        url: `/admin/user/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Users"],
    }),

    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/user/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),

    // profile
    getUserProfile: builder.query<UserProfile, void>({
      query: () => "/user/profile",
      providesTags: ["Profile"],
    }),

    updateUserProfile: builder.mutation<
      { data: UserProfile },
      Partial<UserProfile>
    >({
      query: (data) => ({
        url: `/user/profile`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Profile"],
    }),

    updateUserPassword: builder.mutation<
      void,
      { currentPassword: string; newPassword: string }
    >({
      query: (data) => ({
        url: `/user/password`,
        method: "PUT",
        body: data,
      }),
    }),

    // admin responder
    getResponders: builder.query<ResponderResponse, PaginationArgs>({
      query: (args) => `/admin/responder?${buildQueryParams(args || {})}`,
      providesTags: ["Responders"],
    }),

    getResponderById: builder.query<Responder, number>({
      query: (id) => `/admin/responder/${id}`,
      providesTags: ["Responders"],
    }),

    createResponder: builder.mutation<Responder, Partial<Responder>>({
      query: (newData) => ({
        url: "/admin/responder",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Responders"],
    }),

    updateResponder: builder.mutation<
      Responder,
      { id: number; data: Partial<Responder> }
    >({
      query: ({ id, data }) => ({
        url: `/admin/responder/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Responders"],
    }),

    deleteResponder: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/responder/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Responders"],
    }),

    // major
    getMajors: builder.query<MajorResponse, PaginationArgs>({
      query: (args) => `/major?${buildQueryParams(args || {})}`,
      providesTags: ["Major"],
    }),

    // requests
    getRequests: builder.query<RequestsResponse, PaginationArgs>({
      query: (args) => `/admin/contact-us?${buildQueryParams(args || {})}`,
      providesTags: ["Requests"],
    }),

    createRequest: builder.mutation<RequestType, Partial<RequestType>>({
      query: (newData) => ({
        url: "/contact-us",
        method: "POST",
        body: newData,
      }),
      invalidatesTags: ["Requests"],
    }),

    updateRequest: builder.mutation<
      RequestType,
      { id: number; data: Partial<RequestType> }
    >({
      query: ({ id, data }) => ({
        url: `/admin/contact-us/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Requests"],
    }),

    deleteRequest: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/contact-us/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Requests"],
    }),
  }),
});

export const {
  // user
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  // profile
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useUpdateUserPasswordMutation,
  // admin responder
  useGetRespondersQuery,
  useLazyGetRespondersQuery,
  useGetResponderByIdQuery,
  useCreateResponderMutation,
  useUpdateResponderMutation,
  useDeleteResponderMutation,
  // major
  useGetMajorsQuery,
  useLazyGetMajorsQuery,
  // requests
  useGetRequestsQuery,
  useCreateRequestMutation,
  useUpdateRequestMutation,
  useDeleteRequestMutation,
} = userApi;
